import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import { STORAGE_KEYS } from '../constants/storageKeys';

/**
 * Получает название устройства на основе User Agent
 * Возвращает строку вида: "WEB - Chrome 120.0 (MacOS 14.0)"
 */
export const getDeviceName = (): string => {
    const parser = new UAParser();
    const result = parser.getResult();

    const browserName = result.browser.name || 'Unknown Browser';
    const browserVersion = result.browser.version || '';
    const osName = result.os.name || 'Unknown OS';
    const osVersion = result.os.version || '';
    const deviceVendor = result.device.vendor || '';
    const deviceModel = result.device.model || '';

    // Если это мобильное устройство
    if (result.device.type === 'mobile' || result.device.type === 'tablet') {
        const deviceInfo = deviceVendor && deviceModel 
            ? `${deviceVendor} ${deviceModel}` 
            : result.device.type === 'mobile' ? 'Mobile Device' : 'Tablet';
        return `${deviceInfo} - ${browserName} (${osName} ${osVersion})`.trim();
    }

    // Для десктопа
    return `WEB - ${browserName} ${browserVersion} (${osName} ${osVersion})`.trim();
};

/**
 * Получает или генерирует уникальный идентификатор устройства
 * ID сохраняется в localStorage для постоянства между сессиями
 * Возвращает уникальный UUID устройства
 */
export const getDeviceId = (): string => {
    try {
        // Пытаемся получить существующий ID из localStorage
        let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);

        if (!deviceId) {
            // Если ID не существует, генерируем новый
            deviceId = uuidv4();
            localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
        }

        return deviceId;
    } catch (error) {
        // Если localStorage недоступен, генерируем временный ID для сессии
        console.warn('localStorage недоступен, используется временный ID устройства');
        return uuidv4();
    }
};

/**
 * Генерирует fingerprint браузера на основе доступных характеристик
 * Используется как альтернатива MAC-адресу для веб-приложений
 * @returns строка с fingerprint браузера
 */
export const getBrowserFingerprint = (): string => {
    const parser = new UAParser();
    const result = parser.getResult();

    // Расширенный интерфейс Navigator с экспериментальными свойствами
    const nav = navigator as Navigator & {
        deviceMemory?: number;
    };

    const components = [
        navigator.userAgent,
        navigator.language,
        new Date().getTimezoneOffset().toString(),
        screen.width + 'x' + screen.height,
        screen.colorDepth.toString(),
        result.browser.name || '',
        result.os.name || '',
        navigator.hardwareConcurrency?.toString() || '',
        nav.deviceMemory?.toString() || '',
    ];

    // Создаем простой хеш из компонентов
    const fingerprint = components.join('|');
    
    // Простая хеш-функция
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(16);
};

/**
 * Получает комбинированный идентификатор устройства
 * Использует сохраненный UUID + fingerprint для большей уникальности
 */
export const getDeviceUniqueId = (): string => {
    const deviceId = getDeviceId();
    // const fingerprint = getBrowserFingerprint();
    
    // return `${deviceId}-${fingerprint}`;
    return `${deviceId}`;
};

/**
 * Определяет название операционной системы
 * @returns Название ОС
 */
export const getDeviceNameOS = (): string => {
    const parser = new UAParser();
    const result = parser.getResult();
    const userAgent = navigator.userAgent;

    // Проверяем по User Agent
    if (/Android/i.test(userAgent)) {
        return 'Android';
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'iOS';
    }
    if (/Windows/i.test(userAgent)) {
        return 'Windows';
    }
    if (/OpenBSD/i.test(userAgent)) {
        return 'OpenBSD';
    }
    if (/SunOS/i.test(userAgent)) {
        return 'SunOS';
    }
    if (/(Linux)|(X11)/i.test(userAgent)) {
        return 'Linux';
    }
    if (/(Mac_PowerPC)|(Macintosh)/i.test(userAgent)) {
        return 'Mac OS';
    }
    if (/QNX/i.test(userAgent)) {
        return 'QNX';
    }
    if (/BeOS/i.test(userAgent)) {
        return 'BeOS';
    }
    if (/OS\/2/i.test(userAgent)) {
        return 'OS/2';
    }

    // Если не удалось определить по User Agent, используем UAParser
    const osName = result.os.name;
    if (osName) {
        if (osName.toLowerCase().includes('android')) return 'Android';
        if (osName.toLowerCase().includes('ios')) return 'iOS';
        if (osName.toLowerCase().includes('windows')) return 'Windows';
        if (osName.toLowerCase().includes('mac')) return 'Mac OS';
        if (osName.toLowerCase().includes('linux')) return 'Linux';
    }

    return 'Other';
};

/**
 * Определяет платформу/браузер
 * @returns Название платформы
 */
export const getPlatform = (): string => {
    const parser = new UAParser();
    const result = parser.getResult();
    const userAgent = navigator.userAgent;

    // Проверяем специфичные браузеры
    if (/YaBrowser|YaApp/i.test(userAgent)) {
        return 'Yandex Browser';
    }
    if (/SamsungBrowser/i.test(userAgent)) {
        return 'Samsung Browser';
    }
    if (/UCBrowser|UCWEB/i.test(userAgent)) {
        return 'UC Browser';
    }
    if (/OPR/i.test(userAgent) && /Version/i.test(userAgent)) {
        return 'Opera Next';
    }
    if (/Opera|OPR/i.test(userAgent)) {
        return 'Opera';
    }
    if (/Edge\/\d+/i.test(userAgent)) {
        return 'Legacy Edge';
    }
    if (/Edg\//i.test(userAgent)) {
        return 'Edge (Chromium)';
    }
    if (/MSIE/i.test(userAgent)) {
        return 'MSIE';
    }
    if (/Trident/i.test(userAgent)) {
        return 'MSIE 11+';
    }
    if (/Firefox/i.test(userAgent)) {
        return 'Firefox';
    }
    if (/Chrome/i.test(userAgent) && !/Edge|Edg|OPR/i.test(userAgent)) {
        return 'Chrome';
    }
    if (/Safari/i.test(userAgent) && !/Chrome|CriOS/i.test(userAgent)) {
        return 'Safari';
    }

    // Используем UAParser как запасной вариант
    const browserName = result.browser.name;
    if (browserName) {
        if (browserName.toLowerCase().includes('yandex')) return 'Yandex Browser';
        if (browserName.toLowerCase().includes('samsung')) return 'Samsung Browser';
        if (browserName.toLowerCase().includes('uc')) return 'UC Browser';
        if (browserName.toLowerCase().includes('opera')) return 'Opera';
        if (browserName.toLowerCase().includes('edge')) return 'Edge (Chromium)';
        if (browserName.toLowerCase().includes('chrome')) return 'Chrome';
        if (browserName.toLowerCase().includes('safari')) return 'Safari';
        if (browserName.toLowerCase().includes('firefox')) return 'Firefox';
        if (browserName.toLowerCase().includes('ie')) return 'MSIE';
    }

    return 'Other';
};

/**
 * Получает версию операционной системы
 * @returns Версия ОС
 */
export const getAppVersion = (): string => {
    const parser = new UAParser();
    const result = parser.getResult();

    const osVersion = result.os.version || '';

    return osVersion || 'Unknown';
};

/**
 * Определяет, открыт ли сайт внутри WebView мобильного приложения
 * (react-native-webview прокидывает этот мост в window)
 */
export const isReactNativeWebView = (): boolean => {
    return Boolean((window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView);
};

